import { createEffect, createSignal, For, useContext } from 'solid-js';
import { StyledCourseCategoryMenuContainer, StyledCourseCategoryMenuContainerInner, StyledCoursePathGrid, StyledCoursePathsContainer, StyledCurriculumContainer, StyledMenuItem, StyledMenuItemButton, StyledMenuItemsList } from './course-path-style';
import { Text } from '../../ui-components/text/text';
import { useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import theme from '../../style/theme';
import { CourseCardData, CourseCard } from '../course-card/course-card';
import { Heading } from '../../ui-components/heading/heading';
import { ExpandableContainer } from '../../ui-components/expandable-container/expandable-container';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { CourseTag } from '../course/course-types';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';

export type PathCategory = {
    categoryName: string;
    paths: PathWithCourses[];
};

type PathWithCourses = {
    id: string;
    name: string;
    curriculums: CurriculumsData[];
};

type CurriculumsData = {
    id: string;
    name: string;
    courses: PathCourse[];
};

type PathCourse = {
    courseId: string;
    name: string;
    description: string;
    progress: number;
    completedDate: string;
    coverImageUrl: {
        small: string;
    };
    durationInSeconds: number;
    tags?: CourseTag[];
    path?: {
        id: string;
        name: string;
    };
    curriculum?: {
        id: string;
        name: string;
    };
}

type CoursePathProps = {
    pathsCategories: PathCategory[];
}

export const CoursePath = (props: CoursePathProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { localize } = useContext(AppContext);

    let expandableContainer: HTMLDivElement | undefined;

    const [showPathCategory, setShowPathCategory] = createSignal<string | null>();

    const paramSlugTranslation = () => localize('course-category-url-parameter', 'category');

    createEffect(() => {
        const categoryParam = searchParams[paramSlugTranslation()];

        if (categoryParam) {
            setShowPathCategory(categoryParam);
        } else {
            const firstCategory = props.pathsCategories?.[0]?.categoryName;
            setShowPathCategory(firstCategory);
        }
    });

    const PathComponent = ({ path }: { path: PathWithCourses }) => {
        return (
            <div id={path.name}>
                <div class="mt-16">
                    <For each={path.curriculums}>{(curriculum) => (
                        <CurriculumComponent curriculum={curriculum}/>
                    )}</For>
                </div>
            </div>
        );
    };

    const CurriculumComponent = ({ curriculum }: { curriculum: PathWithCourses['curriculums'][0] }) => {
        return (
            <StyledCurriculumContainer>
                <Heading tag='span' variant='tinyRed' textTransform='uppercase' noBlockSpacing>
                    {curriculum.name}
                </Heading>

                <StyledCoursePathsContainer>
                    <StyledCoursePathGrid>
                        <For each={curriculum.courses}>{(course: CourseCardData) => (
                            <CourseCard course={course} />
                        )}</For>
                    </StyledCoursePathGrid>
                </StyledCoursePathsContainer>
            </StyledCurriculumContainer>
        );
    };

    const PathCategoryDropdown = () => (
        <StyledCourseCategoryMenuContainerInner>
            <ExpandableContainer
                ref={expandableContainer}
                enableOutsideClickToCloseOnDesktop={true}
                isDropdown={true}
                shrinkOnTablet={true}
                backgroundColor={theme.palette.white}
                chevronColor={theme.palette.darkGray}
                chevronSize={1.8}
                headingProps={{
                    children: showPathCategory(),
                    tag: 'h4',
                    noBlockSpacing: true,
                    variant: 'tinyGray'
                }}
                customHeadingRowCss={`
                    padding-block: 2rem;
                    padding-inline: 0.5rem;
                    ${theme.mediaQueries.tabletMaxOriginal} {
                        padding-block: 1rem;
                    }
                `}
            >
                <StyledMenuItemsList>
                    <For each={props.pathsCategories}>{(pathCategory: PathCategory) => (
                        <StyledMenuItem>
                            <StyledMenuItemButton
                                disabled={pathCategory.categoryName === showPathCategory()}
                                onClick={() => {
                                    expandableContainer?.click();
                                    setShowPathCategory(pathCategory.categoryName);
                                    navigate(location.pathname + `?${paramSlugTranslation()}=${pathCategory.categoryName}`, { scroll: false });
                                }}
                                aria-label={localize('show-category', 'Show Category')}
                            >
                                <Text
                                    fontSize='small'
                                    fontStyle={pathCategory.categoryName === showPathCategory() ? 'bold' : 'normal'}
                                    color='defer'
                                    noBlockSpacing
                                >
                                    {pathCategory.categoryName}
                                </Text>
                            </StyledMenuItemButton>
                        </StyledMenuItem>
                    )}</For>
                </StyledMenuItemsList>
            </ExpandableContainer>
        </StyledCourseCategoryMenuContainerInner>
    );

    return (
        <ErrorCatcher componentName='CoursePath'>
            <StyledCourseCategoryMenuContainer>
                <Heading tag='h4'>
                    {localize('select-area-of-care-for-courses', 'Select area of care')}
                </Heading>
                
                <PathCategoryDropdown />
            </StyledCourseCategoryMenuContainer>

            <For each={props.pathsCategories.filter(path => path.categoryName === showPathCategory())}>{(pathCategory) => (
                <For each={pathCategory.paths}>{(path) => (
                    <PathComponent path={path} />
                )}</For>
            )}</For>

            <ShowpadDelayInfoBox text={localize('showpad-progress-delay-info-text', 'Please note that it may take up to an hour for your progress to appear')}/>
        </ErrorCatcher>
    );
};

